import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'

import { Loader } from '@/components/loaders/Loader'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { ExternalLink } from '@/components/ExternalLink'
import { Pill } from '@/components/pills/Pill'

import { fetchChangedCalls } from '@/reducers/promptimizer/promptimizer.actions'
import { REGRESSION_SCOPE } from '@/reducers/promptimizer/promptimizer.redux'
import { aiResponseToPassFail, isFailResult, isPassResult } from '@/utils/promptFeedback.helpers'

const FIXED = 'fixed'
const BROKE = 'broke'

// The Calibration Agent's regression calls have no reviewed answer: their reference is the current prompt's own
// answer, so a change can't be called right or wrong — only shown.
const changeLabel = (call, scope) => {
  if (scope === REGRESSION_SCOPE) {
    return 'What changed'
  }
  return call.change === FIXED ? 'Now correct' : 'Now wrong'
}

/**
 * One prompt's answer on one call.
 *
 * A missing answer is NOT a third value alongside yes/no/na, and rendering it in the same slot as one ("this
 * version said nothing usable") reads as though the model chose it. It means the prompt returned something that
 * could not be read as an answer at all — a real and serious finding about that prompt, scored as wrong.
 */
const Answer = ({ value }) =>
  isNil(value) ? (
    <em className="copilot-optimize__call-no-answer">gave no readable answer</em>
  ) : (
    <>
      said <strong>{value}</strong>
    </>
  )

const Result = ({ value, mustBePresent }) => {
  if (isNil(value)) {
    return <em className="copilot-optimize__call-no-answer">no readable answer</em>
  }
  const label = aiResponseToPassFail(value, mustBePresent)
  return (
    <Pill xs outlined success={isPassResult(label)} critical={isFailResult(label)}>
      {label}
    </Pill>
  )
}

/**
 * The calls a version answered differently, and what the reference says about each.
 *
 * This is the evidence behind "fixes 9, breaks 2". Without it the result is a number to be trusted; with it a
 * reviewer can check the claim on the calls they know, which is the only way anyone gets comfortable enough to
 * ship a prompt they didn't write.
 *
 * ⚠ EACH ROW USED TO OFFER "keep as a test case", REMOVED 2026-08-09 rather than finished. The idea is right and
 * it belongs here — this is the moment someone has an opinion about a call, and the run's reference answer is
 * exactly the expected answer a test wants. But a save left the criterion's test list stale until a reload, and a
 * reload lost the record of which rows had been taken, so it read as though nothing had been saved. Both need
 * state this panel does not have; neither is MVP. Full write-up in `observations_and_tasks.md` (2026-08-09).
 */
export const ChangedCallsList = ({
  optimizationId,
  candidateId,
  criteriaScid,
  scope,
  mustBePresent,
}) => {
  const dispatch = useDispatch()
  const storageKey = scope ? `${candidateId}:${scope}` : candidateId
  // Both keyed by candidate, so opening one candidate's list neither clobbers another's rows nor spins its spinner.
  const calls = useSelector((state) => state.promptimizer?.changedCalls?.[storageKey])
  const loading = useSelector((state) => state.promptimizer?.callsLoading?.[storageKey] === true)
  const error = useSelector((state) => state.promptimizer?.callsError?.[storageKey])

  useEffect(() => {
    if (isNil(calls)) {
      dispatch(fetchChangedCalls({ optimizationId, candidateId, criteriaScid, scope }))
    }
  }, [dispatch, optimizationId, candidateId, calls, criteriaScid, scope])

  if (isNil(calls)) {
    if (!loading && !isNil(error)) {
      return <ErrorMessage content={error.message} />
    }
    return (
      <div className="copilot-optimize__calls-loading">
        <Loader centered />
      </div>
    )
  }

  if (isEmpty(calls)) {
    return (
      <p className="copilot-optimize__panel-note">
        This version answered every measured call the same way your prompt does.
      </p>
    )
  }

  return (
    <ul
      className={classNames('copilot-optimize__calls', {
        'copilot-optimize__calls--scroll': scope === REGRESSION_SCOPE,
      })}
    >
      {calls.map((call) => (
        <li
          key={call.call_id}
          className={classNames('copilot-optimize__call', {
            'is-fixed': scope !== REGRESSION_SCOPE && call.change === FIXED,
            'is-broke': scope !== REGRESSION_SCOPE && call.change === BROKE,
          })}
        >
          <div className="copilot-optimize__call-head">
            <ExternalLink url={`/calls/${call.call_id}`} label={`Call ${call.call_id}`} />
            <span className="copilot-optimize__call-change">{changeLabel(call, scope)}</span>
          </div>

          {scope === REGRESSION_SCOPE ? (
            <div
              className="copilot-optimize__call-answers flex-align-center medium-gap"
              data-testid={`call-answers-${call.call_id}`}
            >
              <span className="flex-align-center small-gap">
                <span>Previously:</span>
                <Result value={call.incumbent_answer} mustBePresent={mustBePresent} />
              </span>
              <span className="flex-align-center small-gap">
                <span>This version:</span>
                <Result value={call.candidate_answer} mustBePresent={mustBePresent} />
              </span>
            </div>
          ) : (
            <p className="copilot-optimize__call-answers">
              Your prompt <Answer value={call.incumbent_answer} />, this version{' '}
              <Answer value={call.candidate_answer} />. The reference says{' '}
              <strong>{call.reference}</strong>.
            </p>
          )}

          {(isNil(call.incumbent_answer) || isNil(call.candidate_answer)) && (
            <p className="copilot-optimize__call-why">
              &ldquo;No readable answer&rdquo; means the prompt returned something that could not be
              read as yes, no or N/A. The run counts that as wrong, because a prompt that cannot
              produce a valid answer is failing the call.
            </p>
          )}

          {!isEmpty(call.explanation) && (
            <p className="copilot-optimize__call-why">{call.explanation}</p>
          )}
        </li>
      ))}
    </ul>
  )
}
